import _ from 'lodash'
import { ActionContext } from 'vuex'
import RootState from '../state'
import ManagerState from './state'
import ContentEndpoint, {
  ContentItem,
  DeleteContentItemRequest,
  GetContentListRequest,
  GetContentListResponse,
  GetContentRequest,
  UpdateContentItemRequest
} from '../../api/gerbera/contents'
import CategoryEndpoint, { CategoryItem, GetCategoryListRequest } from '../../api/gerbera/categories'
import MekajikiEndpoint, { GetKajikiDataRequest, KajikiDbItem } from '../../api/kajiki/kajiki'
import LoginAuthentication from '../../api/kajiki/account'
import { ContentOrderBy } from '../../api/gerbera/core'
import { Getters, Mutations } from './helpers'
import { ListItemLoader } from '../../api/kajiki/core'

type ManagerContext = ActionContext<ManagerState, RootState>

export { ContentOrderBy }

export interface GetContentListParam {
  page?: number
  perPage?: number
  categoryId?: number[]
  status?: 'published' | 'reserved' | 'pending',
  title?: string
  orderBy?: ContentOrderBy[]
}

// GerberaAPIから情報を取得する
export async function loadArticles(context: ManagerContext, request: GetContentListRequest): Promise<GetContentListResponse> {
  const perPage = request.perPage || 20
  const param: GetContentListRequest = Object.assign({
    perPage
  }, request)

  return new ContentEndpoint()
    .getContentList(param)
    .then((response) => {
      const isFirstPage = !request.page || request.page <= 1
      if (response.count === 0 && !isFirstPage) {
        // 記事が削除されたことが原因で、現在のページ数が合計のページ数を上回るケースがある
        // その場合、最後のページを指定して再検索する
        const lastPage = Math.ceil(response.total / perPage)
        return loadArticles(context, Object.assign({}, request, <GetContentListRequest>{
          page: lastPage
        }))
      }
      Mutations.setArticlesData(context, response.items)
      Mutations.setBaseData(context, response)
      return Promise.resolve(response)
    })
}

export async function copyContentItem(context: ManagerContext, request: GetContentRequest): Promise<ContentItem> {
  return new ContentEndpoint()
    .copyContentItem(request)
}

export async function selectedItemUpdate(context: ManagerContext, request: ContentItem): Promise<number> {
  const selected = Getters.selectedArticleIds(context)
  if (selected.length > 0) {
    const params: UpdateContentItemRequest = Object.assign({
      siteId: Getters.getAcountData(context).kajikiId,
      contentId: selected
    }, request)
    return updateContentItem(context, params)
  }
  return Promise.resolve(0)
}

export async function updateContentItem(context: ManagerContext, request: UpdateContentItemRequest): Promise<number> {
  return new ContentEndpoint()
    .updateContentItem(request)
    .then((response) => {
      // promise で更新件数を返す
      return response.length
    })
}

export async function selectedItemDelete(context: ManagerContext): Promise<number> {
  const selected = Getters.selectedArticleIds(context)
  if (selected.length > 0) {
    return deleteContentItem(context, {
      siteId: Getters.getAcountData(context).kajikiId,
      contentId: selected
    })
  }
  return Promise.resolve(0)
}

export async function deleteContentItem(context: ManagerContext, request: DeleteContentItemRequest): Promise<number> {
  return new ContentEndpoint()
    .deleteContentItem(request)
}

export async function loadStorageData(context: ManagerContext): Promise<void> {
  const param: number = Getters.getAcountData(context).kajikiId
  return new ContentEndpoint()
    .loadStorageData(param)
    .then((response) => {
      Mutations.setStorageData(context, response)
    })
}

// ウェブサイト側の情報を取得する

export interface MekajikiParam {
  page?: number
  perPage?: number
}

interface KajikiSelection {
  kajikiList: KajikiDbItem[]
  selected: KajikiDbItem
}

export async function loadKajikiData(context: ManagerContext, kajikiId: number): Promise<void> {
  const loader = new ListItemLoader<number, KajikiDbItem>((siteId, page, perPage) => {
    const param: GetKajikiDataRequest = {
      siteId,
      page,
      perPage
    }
    return new MekajikiEndpoint().getKajikiData(param)
  })
  loader.perPage = 100

  Mutations.setLoadingKajiki(context, true)
  Mutations.setLoadingArticle(context, true)
  return loader.load(kajikiId)
    .then((items: KajikiDbItem[]) => {
      return new Promise<KajikiSelection>((resolve, reject) => {
        // mekajiki-manage で Kajiki を検索する
        const kajikiList = items
        const selected = kajikiList.find(kajiki => kajiki.kajikiId === kajikiId)
        if (selected) {
          // Kajiki が正しかった
          resolve({
            kajikiList,
            selected
          })
          return
        }
        // 通信は成功しているが、指定されたIDのKajikiが見つからなかった時404を返す
        reject({
          response: {
            status: 404,
            statusText: 'Not Found'
          }
        })
      })
    })
    .then((selection) => {
      // ユーザー名を取得する
      return new LoginAuthentication().getLoginAuthentication({
        siteId: kajikiId
      })
        .then((response) => {
          Mutations.setInitialKajikiData(context, {
            kajikiList: selection.kajikiList,
            selected: selection.selected,
            userName: response.userName,
            accountName: response.accountName,
            sso: response.sso
          })
        })
    }).then(() => {
      Mutations.setLoadingKajiki(context, false)
    })
}

export async function loadCategories(context: ManagerContext, request: number | GetCategoryListRequest): Promise<void> {
  const loader = new ListItemLoader<GetCategoryListRequest, CategoryItem>((req, page, perPage) => {
    const param: GetCategoryListRequest = {
      page,
      perPage,
      siteId: req.siteId,
      orderBy: req.orderBy
    }

    return new CategoryEndpoint().getCategoryList(param)
  })
  loader.perPage = 100

  const baseRequest: GetCategoryListRequest = _.isNumber(request) ? { siteId: request } : request
  return loader.load(baseRequest)
    .then((categories) => {
      Mutations.setCategoryData(context, categories)
    })
}

export async function loadCategoryWhole(context: ManagerContext, siteId: number): Promise<void> {
  return new CategoryEndpoint()
    .getCategoryWhole(siteId)
    .then((categories) => {
      Mutations.setCategoryWholeData(context, categories)
    })
}

export default {
  loadArticles,
  loadKajikiData,
  copyContentItem,
  updateContentItem,
  deleteContentItem,
  selectedItemUpdate,
  selectedItemDelete,
  loadStorageData,
  loadCategories,
  loadCategoryWhole
}
